import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Avatar, theme } from 'antd';
import {
  DashboardOutlined,
  ApartmentOutlined,
  FileTextOutlined,
  SearchOutlined,
  ToolOutlined,
  BookOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  BarChartOutlined,
  FileProtectOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../stores/auth.store';
import { Role } from '../hooks/useAuth';

const { Header, Sider, Content } = Layout;

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  roles: Role[];
}

const menuItems: MenuItem[] = [
  { key: 'dashboard', icon: <DashboardOutlined />, label: '数据看板', path: '/dashboard', roles: [Role.ADMIN, Role.OPS, Role.TECH] },
  { key: 'channels', icon: <ApartmentOutlined />, label: '渠道管理', path: '/channels', roles: [Role.ADMIN, Role.OPS] },
  { key: 'orders', icon: <FileTextOutlined />, label: '链接单监控', path: '/orders', roles: [Role.ADMIN, Role.OPS, Role.TECH, Role.PARTNER, Role.DOWNSTREAM] },
  { key: 'reports', icon: <SearchOutlined />, label: '回传查询', path: '/reports', roles: [Role.ADMIN, Role.OPS, Role.TECH, Role.PARTNER, Role.DOWNSTREAM] },
  { key: 'tracking', icon: <BarChartOutlined />, label: '投放分析', path: '/tracking', roles: [Role.ADMIN, Role.OPS, Role.TECH] },
  { key: 'policies', icon: <FileProtectOutlined />, label: '保单监控', path: '/policies', roles: [Role.ADMIN, Role.OPS, Role.TECH] },
  { key: 'quarantine', icon: <InboxOutlined />, label: '隔离事件', path: '/quarantine', roles: [Role.ADMIN, Role.OPS, Role.TECH] },
  { key: 'crypto-tool', icon: <ToolOutlined />, label: '加密工具', path: '/crypto-tool', roles: [Role.TECH] },
  { key: 'api-docs', icon: <BookOutlined />, label: '接口文档', path: '/api-docs', roles: [Role.ADMIN, Role.OPS, Role.TECH, Role.DOWNSTREAM] },
  { key: 'profile', icon: <UserOutlined />, label: '个人中心', path: '/profile', roles: [Role.ADMIN, Role.OPS, Role.TECH, Role.PARTNER, Role.DOWNSTREAM] },
];

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const currentRole = (user?.role as Role) || Role.DOWNSTREAM;

  const filteredMenu = menuItems
    .filter((item) => item.roles.includes(currentRole))
    .map((item) => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
    }));

  const selectedKey = menuItems.find((m) => location.pathname.startsWith(m.path))?.key || 'dashboard';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: '个人中心', onClick: () => navigate('/profile') },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} style={{ background: '#1E3A8A' }}>
        <div style={{ height: 48, margin: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: collapsed ? 14 : 18, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          {collapsed ? '智' : '智保科技'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={filteredMenu}
          onClick={({ key }) => {
            const item = menuItems.find((m) => m.key === key);
            if (item) navigate(item.path);
          }}
          style={{ background: 'transparent' }}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: '0 24px', background: colorBgContainer, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar icon={<UserOutlined />} style={{ background: '#3B82F6' }} />
              <span>{user?.displayName || user?.username || '用户'}</span>
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: 24,
            padding: 24,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            minHeight: 280,
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
